<script lang="ts">
	/**
	 * MagneticButton - Button/link that follows cursor with magnetic effect
	 *
	 * @example
	 * <MagneticButton href="/contact" class="btn-primary">
	 *   Get Started
	 * </MagneticButton>
	 *
	 * <MagneticButton strength={0.3} class="btn-secondary">
	 *   Learn More
	 * </MagneticButton>
	 */
	import type { Snippet } from 'svelte';
	import { prefersReducedMotion, isMobileDevice } from '$lib/utils/motion';

	interface Props {
		/** URL for anchor element (renders as button if omitted) */
		href?: string;
		/** Additional CSS classes */
		class?: string;
		/** Magnetic effect strength (0-1) */
		strength?: number;
		children: Snippet;
	}

	let { href, class: className = '', strength = 0.2, children }: Props = $props();

	let element: HTMLElement;
	let x = $state(0);
	let y = $state(0);
	let _isHovered = $state(false);

	function handleMouseMove(e: MouseEvent) {
		if (!element || prefersReducedMotion() || isMobileDevice()) return;
		const rect = element.getBoundingClientRect();
		const centerX = rect.left + rect.width / 2;
		const centerY = rect.top + rect.height / 2;

		x = (e.clientX - centerX) * strength;
		y = (e.clientY - centerY) * strength;
	}

	function handleMouseLeave() {
		x = 0;
		y = 0;
		_isHovered = false;
	}

	function handleMouseEnter() {
		_isHovered = true;
	}
</script>

<svelte:element
	this={href ? 'a' : 'button'}
	{href}
	bind:this={element}
	class="inline-block {className}"
	style="transform: translate({x}px, {y}px); transition: transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)"
	onmousemove={handleMouseMove}
	onmouseleave={handleMouseLeave}
	onmouseenter={handleMouseEnter}
	role={href ? undefined : 'button'}
>
	{@render children()}
</svelte:element>
